import React,{useState,useMemo,useCallback} from 'react';
import Icon from '../Text/Icon';
import Text from '../Text/Text';

// Lookup helper: find a node's display text by ID
function getNodeText(nodeId,currentNodes) {
    const node=currentNodes?.find(n => n.id===nodeId);
    return node?.data?.text||nodeId;
}

// Truncate long text
function truncate(str,max=40) {
    if(!str) return '';
    return str.length>max? str.slice(0,max)+'…':str;
}

function Checkbox({checked,onChange}) {
    return (
        <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            className="flex-shrink-0 w-3.5 h-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
        />
    );
}

export default function ActionPreview({changes,currentNodes=[],onAccept,onReject}) {
    const adds=useMemo(() => changes?.nodesToAdd||[],[changes]);
    const mods=useMemo(() => changes?.nodesToModify||[],[changes]);
    const dels=useMemo(() => changes?.nodesToDelete||[],[changes]);
    const edgeAdds=useMemo(() => changes?.edgesToAdd||[],[changes]);
    const edgeDels=useMemo(() => changes?.edgesToDelete||[],[changes]);
    const positions=useMemo(() => changes?.newPositions||[],[changes]);
    const suggestions=useMemo(() => changes?.suggestions||[],[changes]);

    // Selection state: keyed by "type-index"
    const [selected,setSelected]=useState({});

    // Reset selection when changes object changes
    const changesRef=React.useRef(changes);
    if(changesRef.current!==changes) {
        changesRef.current=changes;
        const init={};
        adds.forEach((_,i) => {init[`add-${i}`]=true;});
        mods.forEach((_,i) => {init[`mod-${i}`]=true;});
        dels.forEach((_,i) => {init[`del-${i}`]=true;});
        if(edgeAdds.length>0) init['edgeAdds']=true;
        if(edgeDels.length>0) init['edgeDels']=true;
        if(positions.length>0) init['positions']=true;
        setSelected(init);
    }

    const toggle=useCallback((key) => {
        setSelected(prev => ({...prev,[key]: !prev[key]}));
    },[]);

    const selectedCount=useMemo(() => Object.values(selected).filter(Boolean).length,[selected]);
    const totalItems=useMemo(() => Object.keys(selected).length,[selected]);
    const allSelected=selectedCount===totalItems&&totalItems>0;

    const toggleAll=useCallback(() => {
        const newVal=!allSelected;
        setSelected(prev => {
            const next={};
            Object.keys(prev).forEach(k => {next[k]=newVal;});
            return next;
        });
    },[allSelected]);

    // Build filtered changes based on selection
    const handleAcceptSelected=useCallback(() => {
        const filtered={
            nodesToAdd: adds.filter((_,i) => selected[`add-${i}`]),
            nodesToModify: mods.filter((_,i) => selected[`mod-${i}`]),
            nodesToDelete: dels.filter((_,i) => selected[`del-${i}`]),
            edgesToAdd: selected['edgeAdds']? edgeAdds:[],
            edgesToDelete: selected['edgeDels']? edgeDels:[],
            newPositions: selected['positions']? positions:[],
            suggestions
        };
        onAccept(filtered);
    },[adds,mods,dels,edgeAdds,edgeDels,positions,suggestions,selected,onAccept]);

    const hasChanges=adds.length+mods.length+dels.length+edgeAdds.length+edgeDels.length+positions.length+suggestions.length>0;
    if(!changes||!hasChanges) return null;

    return (
        <div className="border-t border-gray-200 bg-white">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                <div className="flex items-center gap-2">
                    <Checkbox checked={allSelected} onChange={toggleAll} />
                    <Text className="text-xs font-semibold text-gray-700">
                        {selectedCount}/{totalItems} selected
                    </Text>
                </div>
                <div className="flex gap-1.5">
                    <button
                        onClick={onReject}
                        className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-white rounded border border-gray-300 transition-colors hover:bg-gray-100 cursor-pointer"
                    >
                        Reject All
                    </button>
                    <button
                        onClick={handleAcceptSelected}
                        disabled={selectedCount===0}
                        className="px-2.5 py-1 text-xs font-medium text-white rounded transition-colors bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                        Accept{selectedCount<totalItems? ` (${selectedCount})`:''}
                    </button>
                </div>
            </div>

            {/* Change list */}
            <div className="overflow-y-auto max-h-48 divide-y divide-gray-100" role="list" aria-label="Proposed changes">
                {/* Nodes to add */}
                {adds.map((node,i) => (
                    <div key={`add-${i}`} role="listitem" aria-label={`Add node: ${truncate(node.data?.text||node.id)}`} className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border-l-4 border-emerald-400">
                        <Checkbox checked={!!selected[`add-${i}`]} onChange={() => toggle(`add-${i}`)} />
                        {/* Icon + symbol for color-blind users (5.5.3/5.5.5) */}
                        <Icon icon="plus-circle-fill" className="flex-shrink-0 text-emerald-600 text-xs !p-0" aria-hidden="true" />
                        <Text className="text-xs text-emerald-900 font-medium truncate flex-1">
                            {truncate(node.data?.text||node.id)}
                        </Text>
                        <span className="text-[10px] text-emerald-600 flex-shrink-0 font-mono bg-emerald-100 px-1 rounded">
                            {node.type?.replace('Node','')||'node'}
                        </span>
                    </div>
                ))}

                {/* Nodes to modify */}
                {mods.map((mod,i) => {
                    const currentText=getNodeText(mod.id,currentNodes);
                    const newText=mod.changes?.text;
                    const newType=mod.changes?.type;
                    return (
                        <div key={`mod-${i}`} role="listitem" aria-label={`Modify node: ${truncate(currentText)}`} className="flex flex-col gap-0.5 px-3 py-1.5 bg-amber-50 border-l-4 border-amber-400">
                            <div className="flex items-center gap-2">
                                <Checkbox checked={!!selected[`mod-${i}`]} onChange={() => toggle(`mod-${i}`)} />
                                <Icon icon="pencil-fill" className="flex-shrink-0 text-amber-600 text-xs !p-0" aria-hidden="true" />
                                <Text className="text-xs text-amber-900 font-medium truncate flex-1">
                                    {truncate(currentText)}
                                </Text>
                                <span className="text-[10px] text-amber-600 flex-shrink-0 font-mono bg-amber-100 px-1 rounded">edit</span>
                            </div>
                            {(newText||newType)&&(
                                <div className="flex items-center gap-2 ml-10">
                                    {newText&&(
                                        <Text className="text-[11px] text-amber-700">
                                            <span className="line-through text-red-400 mr-1">{truncate(currentText,25)}</span>
                                            <span className="text-emerald-700">→ {truncate(newText,25)}</span>
                                        </Text>
                                    )}
                                    {newType&&!newText&&(
                                        <Text className="text-[11px] text-amber-700">
                                            shape → <span className="font-medium">{newType.replace('Node','')}</span>
                                        </Text>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Nodes to delete */}
                {dels.map((nodeId,i) => (
                    <div key={`del-${i}`} role="listitem" aria-label={`Remove node: ${truncate(getNodeText(nodeId,currentNodes))}`} className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border-l-4 border-red-400">
                        <Checkbox checked={!!selected[`del-${i}`]} onChange={() => toggle(`del-${i}`)} />
                        <Icon icon="trash-fill" className="flex-shrink-0 text-red-600 text-xs !p-0" aria-hidden="true" />
                        <Text className="text-xs text-red-900 font-medium line-through truncate flex-1">
                            {truncate(getNodeText(nodeId,currentNodes))}
                        </Text>
                        <span className="text-[10px] text-red-600 flex-shrink-0 font-mono bg-red-100 px-1 rounded">remove</span>
                    </div>
                ))}

                {/* Edges */}
                {edgeAdds.length>0&&(
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50/40">
                        <Checkbox checked={!!selected['edgeAdds']} onChange={() => toggle('edgeAdds')} />
                        <span className="flex-shrink-0 w-4 text-center text-xs font-bold text-emerald-600">+</span>
                        <Text className="text-xs text-emerald-700">
                            {edgeAdds.length} connection{edgeAdds.length!==1? 's':''}
                        </Text>
                    </div>
                )}
                {edgeDels.length>0&&(
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50/40">
                        <Checkbox checked={!!selected['edgeDels']} onChange={() => toggle('edgeDels')} />
                        <span className="flex-shrink-0 w-4 text-center text-xs font-bold text-red-500">−</span>
                        <Text className="text-xs text-red-600">
                            {edgeDels.length} connection{edgeDels.length!==1? 's':''}
                        </Text>
                    </div>
                )}

                {/* Layout repositioning */}
                {positions.length>0&&(
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50/40">
                        <Checkbox checked={!!selected['positions']} onChange={() => toggle('positions')} />
                        <Icon icon="arrows-move" className="flex-shrink-0 w-4 text-xs text-purple-500" />
                        <Text className="text-xs text-purple-700">
                            Reposition {positions.length} node{positions.length!==1? 's':''}
                        </Text>
                    </div>
                )}

                {/* Suggestions */}
                {suggestions.map((s,i) => (
                    <div key={`sug-${i}`} className="flex items-center gap-2 px-3 py-1.5 bg-blue-50/40">
                        <Icon icon="lightbulb" className="flex-shrink-0 w-4 text-xs text-blue-500" />
                        <Text className="text-xs text-blue-700 truncate">
                            {truncate(s.text||s.description||'Suggestion',50)}
                        </Text>
                    </div>
                ))}
            </div>
        </div>
    );
}
